/**
 * Sanitizes attributes for use on a Custom Element tag.
 */
export function getCustomElementAttributes(attrs: Record<string, any> = {}): Record<string, any> {
  const sanitized: Record<string, any> = {};

  for (const [key, value] of Object.entries(attrs)) {
    // custom elements use `class` and not `className`
    if (key === `className`) {
      sanitized.class = value;

      continue;
    }

    // `style` is a React prop holding a mapping of style properties to values rather than an
    // attribute, and React rejects a string there, so it passes through unserialized.
    if (key === `style`) {
      sanitized.style = value;

      continue;
    }

    // custom elements need special handling for boolean attributes
    if (typeof value === `boolean`) {
      sanitized[key] = value ? `` : undefined; // set to empty string for true, remove for false

      continue;
    }

    // serialize objects/arrays as json
    if (typeof value === `object` && value !== null) {
      try {
        sanitized[key] = JSON.stringify(value);
      } catch {
        // ignore
      }

      continue;
    }

    // pass through all else
    sanitized[key] = value;
  }

  return sanitized;
}

/**
 * Sanitizes attributes for parity with Snabbdom.
 */
export function getStandardAttributes(attrs: Record<string, any> = {}): Record<string, any> {
  // snabbdom treats certain attribute values differently
  return Object.fromEntries(
    Object.entries(attrs || {}).map(([key, value]) => {
      let val = value;

      if (val === true) {
        val = ``;
      }

      if (val === null) {
        val = `null`;
      }

      if (val === false) {
        val = undefined;
      }

      return [key, val];
    }),
  );
}
